import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F3ED' }}>
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#C4956A' }} />
    </div>
  );
  if (user) return <Navigate to="/admin/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(' ') : 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#F5F3ED' }}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="w-full max-w-md"
      >
        <div className="rounded-xl p-8 md:p-10 border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
          <div className="text-center mb-8">
            <h1 data-testid="login-title" className="font-serif text-3xl tracking-tight mb-2" style={{ color: '#1A1A1A' }}>
              {t('admin.login.title')}
            </h1>
            <p className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.5)' }}>
              {t('admin.login.subtitle')}
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div data-testid="login-error" className="text-sm p-3 rounded-lg" style={{ backgroundColor: 'rgba(232,93,58,0.08)', color: '#E85D3A' }}>
                {error}
              </div>
            )}
            <div>
              <label className="font-sans text-xs font-medium mb-1.5 block" style={{ color: 'rgba(26,26,26,0.6)' }}>{t('admin.login.email')}</label>
              <Input data-testid="login-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} required
                className="h-11 rounded-lg border-[rgba(26,26,26,0.12)] focus:ring-[#C4956A] focus:border-[#C4956A]"
                style={{ backgroundColor: '#FDFBF7' }} placeholder="admin@store.com" />
            </div>
            <div>
              <label className="font-sans text-xs font-medium mb-1.5 block" style={{ color: 'rgba(26,26,26,0.6)' }}>{t('admin.login.password')}</label>
              <Input data-testid="login-password-input" type="password" value={password} onChange={e => setPassword(e.target.value)} required
                className="h-11 rounded-lg border-[rgba(26,26,26,0.12)] focus:ring-[#C4956A] focus:border-[#C4956A]"
                style={{ backgroundColor: '#FDFBF7' }} placeholder="Enter your password" />
            </div>
            <Button data-testid="login-submit-button" type="submit" disabled={submitting}
              className="w-full h-11 rounded-lg font-sans text-sm font-medium tracking-wider uppercase transition-all duration-300"
              style={{ backgroundColor: '#E85D3A', color: '#FFFFFF' }}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('admin.login.submit')}
            </Button>
          </form>
        </div>
        <p className="text-center mt-6 font-sans text-xs" style={{ color: 'rgba(26,26,26,0.35)' }}>
          Florashop Admin
        </p>
      </motion.div>
    </div>
  );
}
